import React, { useEffect, useState } from 'react';
import {
  Modal, Box, Button, Typography, TextField,
  Autocomplete, Avatar, IconButton, Tooltip, Divider
} from '@mui/material';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import config from '../config/config';
import DoubleConfirmModal from './DoubleConfirmModal';
import SimpleSuccessSpinner from './LoadingSpinner';
import SnackbarAlert from './SnackbarAlert';

const ACCENT = '#0655A5';

const card = {
  p: 2,
  border: '1px solid #e5e7eb',
  borderRadius: 2,
  bgcolor: 'white'
};

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#fbfbfd',
    transition: 'box-shadow .15s, border-color .15s',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
    '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(6,85,165,0.12)' },
  },
  '& .MuiInputLabel-root': { fontWeight: 600 },
  '& .MuiFormHelperText-root': { ml: 0 },
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 520,
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: 24,
  p: 0,
  maxHeight: '85vh',
  overflow: 'hidden'
};

export default function ModalDepartamento({
  open, onClose, onSave,
  formData, handleInputChange,
  usuario, empresaId
}) {
  const [empresas, setEmpresas] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  useEffect(() => {
    if (!open) return;
    setErrors({});
    (async () => {
      try {
        const res = await axios.post(`${config.apiBaseUrl}/empresasDisponibles`, { idEmpresa: empresaId });
        const lista = res.data || [];
        setEmpresas(lista);

        // default para empresaId === 4 si no hay IDEMPRESA
        if (empresaId === 4 && lista.length > 0 && !formData.IDEMPRESA) {
          handleInputChange('IDEMPRESA', lista[0].EMPRESAID);
        }
      } catch (err) {
        console.error('Error al cargar empresas:', err);
        setEmpresas([]);
      }
    })();
  }, [open, empresaId, formData.IDEMPRESA, handleInputChange]);

  const validate = () => {
    const newErrors = {};
    if (!formData.NOMBRE_DEPARTAMENTO?.trim()) newErrors.NOMBRE_DEPARTAMENTO = 'Nombre obligatorio';
    if (!formData.IDEMPRESA) newErrors.IDEMPRESA = 'Seleccione una empresa';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGuardar = () => {
    if (!validate()) return;
    setConfirmSaveOpen(true);
  };

  const handleConfirmGuardar = async () => {
    setLoading(true);
    try {
      const payload = {
        DEPARTAMENTOID: formData.DEPARTAMENTOID || null,
        NOMBRE_DEPARTAMENTO: formData.NOMBRE_DEPARTAMENTO,
        IDEMPRESA: formData.IDEMPRESA,
        SESSION: usuario
      };

      await axios.post(`${config.apiBaseUrl}/crearEditarDepartamento`, payload);

      setSnackbarMessage(
        payload.DEPARTAMENTOID ? 'Departamento actualizado' : 'Departamento creado'
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      onSave?.();
      onClose();
    } catch (err) {
      console.error('❌ Error al guardar departamento:', err);
      setSnackbarMessage('Error al guardar departamento');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={style}>
          {/* Header azul */}
          <Box
            sx={{
              p: 1.5,
              background: `linear-gradient(90deg, ${ACCENT} 0%, #0B6BCB 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid rgba(255,255,255,.15)'
            }}
          >
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'white', color: ACCENT }}>
              <BusinessRoundedIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
                {formData.DEPARTAMENTOID ? 'Editar Departamento' : 'Nuevo Departamento'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.95 }}>
                Completa los datos y asigna la empresa
              </Typography>
            </Box>
            <Tooltip title="Cerrar">
              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Body */}
          <Box sx={{ p: 2, overflowY: 'auto' }}>
            {/* Datos del departamento */}
            <Box sx={{ ...card, mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                Datos del departamento
              </Typography>

              <TextField
                label="Nombre del Departamento"
                fullWidth
                value={formData.NOMBRE_DEPARTAMENTO || ''}
                onChange={(e) => {
                  handleInputChange('NOMBRE_DEPARTAMENTO', e.target.value);
                  if (errors.NOMBRE_DEPARTAMENTO) {
                    setErrors(prev => ({ ...prev, NOMBRE_DEPARTAMENTO: '' }));
                  }
                }}
                margin="dense"
                error={Boolean(errors.NOMBRE_DEPARTAMENTO)}
                helperText={errors.NOMBRE_DEPARTAMENTO}
                sx={elegantInputSx}
              />
            </Box>

            {/* Empresa */}
            <Box sx={{ ...card }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                Empresa
              </Typography>

              <Autocomplete
                options={empresas.map(e => ({ value: e.EMPRESAID, label: e.NOMBRE_EMPRESA }))}
                getOptionLabel={(opt) => opt.label || ''}
                value={
                  empresas.length
                    ? empresas
                        .map(e => ({ value: e.EMPRESAID, label: e.NOMBRE_EMPRESA }))
                        .find(opt => opt.value === formData.IDEMPRESA) || null
                    : null
                }
                onChange={(_, newVal) => {
                  handleInputChange('IDEMPRESA', newVal ? newVal.value : '');
                  if (errors.IDEMPRESA) {
                    setErrors(prev => ({ ...prev, IDEMPRESA: '' }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Seleccionar empresa"
                    margin="dense"
                    error={Boolean(errors.IDEMPRESA)}
                    helperText={errors.IDEMPRESA}
                    sx={elegantInputSx}
                  />
                )}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                disabled={!empresas.length}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="space-between">
              <Button variant="outlined" onClick={onClose}>Cancelar</Button>
              <Button variant="contained" onClick={handleGuardar}>Guardar</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <DoubleConfirmModal
        open={confirmSaveOpen}
        message="¿Deseas guardar los cambios?"
        handleConfirm={() => {
          setConfirmSaveOpen(false);
          handleConfirmGuardar();
        }}
        handleCancel={() => setConfirmSaveOpen(false)}
      />

      <SimpleSuccessSpinner open={loading} handleClose={() => {}} message="Guardando..." />

      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
      />
    </>
  );
}